import { ComponentType, lazy } from "react";

/**
 * lazyWithRetry — drop-in replacement for React.lazy that survives the two
 * single biggest "endless spinner" failure modes in production SPAs:
 *
 *   1. Stale chunk after deploy. A user has the OLD index.html open in a tab.
 *      We ship a new build. New chunk hashes; the old hash no longer exists
 *      on the CDN. The user navigates → React.lazy tries to fetch
 *      `/assets/CommunityDashboard-OLD.js` → 404 → import rejects.
 *      Stock React.lazy: error surfaces to the error boundary on FIRST try.
 *      lazyWithRetry: on first failure we set a sessionStorage flag and
 *      force a single hard reload. The hard reload picks up the new
 *      index.html, learns the new hash, and the next attempt succeeds.
 *      We never reload twice in a row (the flag is consumed on success).
 *
 *   2. Transient CDN hiccup / flaky network. Chunk fetch hangs, then
 *      eventually rejects with TypeError("Failed to fetch dynamically
 *      imported module"). We retry the import N times with exponential
 *      backoff (50ms, 200ms, 800ms) before giving up.
 *
 * After all in-process retries fail, we throw a TRUE error so the
 * RouteErrorBoundary catches it and shows its CTA. This eliminates the
 * "endless white-screen spinner" class of bugs platform-wide.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
  // Identifies this lazy import in sessionStorage so we don't reload twice
  // in a row for the same module on the same tab.
  retryKey?: string,
): ReturnType<typeof lazy<T>> {
  const STORAGE_KEY = `lazy-reload-${retryKey ?? "anonymous"}`;

  return lazy(async () => {
    // Two phases:
    //   Phase A (in-process retries): handle transient network hiccups.
    //   Phase B (hard-reload fallback): handle stale-chunk-after-deploy.

    const delays = [50, 200, 800];
    let lastErr: unknown = null;

    for (let attempt = 0; attempt <= delays.length; attempt++) {
      try {
        const mod = await factory();
        // Success — clear any stale "we just reloaded for this" flag so a
        // future stale-chunk situation can trigger another reload.
        try {
          window.sessionStorage.removeItem(STORAGE_KEY);
        } catch { /* ignore */ }
        return mod;
      } catch (err) {
        lastErr = err;
        if (attempt < delays.length) {
          await new Promise(r => setTimeout(r, delays[attempt]));
        }
      }
    }

    // All in-process retries failed. Decide whether this is a chunk-hash
    // mismatch (stale deploy) and we should hard-reload, or a real error.
    const msg = (lastErr as any)?.message ?? "";
    const name = (lastErr as any)?.name ?? "";
    const isChunkError =
      name === "ChunkLoadError" ||
      /Loading chunk [\d]+ failed/i.test(msg) ||
      /Loading CSS chunk/i.test(msg) ||
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg);

    if (isChunkError) {
      let alreadyReloaded = false;
      try {
        alreadyReloaded = window.sessionStorage.getItem(STORAGE_KEY) === "1";
      } catch { /* ignore */ }

      if (!alreadyReloaded) {
        try {
          window.sessionStorage.setItem(STORAGE_KEY, "1");
        } catch { /* ignore */ }
        // One-shot hard reload to pick up the new index.html + chunk hashes.
        window.location.reload();
        // Return a never-resolving promise so Suspense keeps showing the
        // spinner for the ~100ms before the page actually reloads, instead
        // of falling through to the error boundary.
        return new Promise(() => {}) as never;
      }
      // Second time around — reload didn't fix it. Fall through so the
      // error boundary can show "A new version is available" with a manual
      // reload button.
    }

    throw lastErr;
  });
}
